import { PUBLICATIONS_API_PATH, PUBLISHING_API_PATH } from "api/api-constants";
import { serverApi } from "api/api-server-actions";
import { PublicationType, SectionType } from "components/server-types";
import { slices, store } from "store/index";
import { withApiErrorReporting } from "utils/api-errors";

export async function serverCreateLinkedSections(
  sectionId: string,
  count: number,
  title: string,
): Promise<void> {
  return withApiErrorReporting("serverCreateLinkedSections", async () => {
    const response = await serverApi.post(`${PUBLISHING_API_PATH}/linked_sections/`, {
      section_id: sectionId,
      count,
      title,
    });
    const sections = response.data as SectionType[];
    store.dispatch(slices.sections.actions.updateMany(sections));
  });
}

export async function serverPublishDraftPublication(publicationId: string): Promise<void> {
  return withApiErrorReporting("serverPublishDraftPublication", async () => {
    const response = await serverApi.post(`${PUBLICATIONS_API_PATH}/${publicationId}/publish/`, {
      publication_id: publicationId,
    });
    const publication = response.data as PublicationType;
    store.dispatch(slices.publications.actions.updateOne(publication));
  });
}

/** Ensures a draft publication exists for a template section (POST …/publications/ensure_draft/). */
export async function serverEnsureDraftPublication(
  sectionId: string,
): Promise<PublicationType | null> {
  return withApiErrorReporting("serverEnsureDraftPublication", async () => {
    const response = await serverApi.post(`${PUBLICATIONS_API_PATH}/ensure_draft/`, {
      section_id: sectionId,
    });
    if (response.status === 204) {
      return null;
    }
    const publication = response.data as PublicationType;
    store.dispatch(slices.publications.actions.updateOne(publication));
    return publication;
  });
}

export async function serverFetchLatestPublications(sectionId?: string): Promise<void> {
  return withApiErrorReporting("serverFetchLatestPublications", async () => {
    const response = await serverApi.get(`${PUBLICATIONS_API_PATH}/latest/`, {
      params: sectionId ? { section_id: sectionId } : undefined,
    });
    const publications = response.data as PublicationType[];
    store.dispatch(slices.publications.actions.updateMany(publications));
  });
}

export type DraftChangeSummary = {
  has_draft: boolean;
  pages_changed: number;
  topics_changed: number;
};

/**
 * Ensure the template section's draft Publication is up to date, then return a
 * summary of how many pages and topics have changed in it
 * (POST …/publications/draft_change_summary/).
 *
 * This is a POST because the backend invokes `SectionPublisher.update_draft`
 * before computing the summary, which may create, update, or delete the draft.
 */
export async function serverFetchDraftChangeSummary(
  sectionId: string,
): Promise<DraftChangeSummary> {
  return withApiErrorReporting("serverFetchDraftChangeSummary", async () => {
    const response = await serverApi.post<DraftChangeSummary>(
      `${PUBLICATIONS_API_PATH}/draft_change_summary/`,
      { section_id: sectionId },
    );
    return response.data;
  });
}
